import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { X, Pen, Star, MessageSquare } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertDialogDescription,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { UniversalCombobox } from '@/components/common/UniversalCombobox';
import { cn } from '@/lib/utils';
import { useReview, useCreateReview, useUpdateReview } from '../hooks';

const reviewSchema = z.object({
  comment: z.string().min(1, 'Comment is required'),
  rating: z.coerce.number().min(1, 'Rating required').max(10),
  userId: z.number().int().positive('User is required'),
  matterId: z.number().int().positive().optional(),
});

type FormValues = z.infer<typeof reviewSchema>;

const MAX_RATING = 10;

interface CreateReviewDialogProps {
  id?: number;
}

export function CreateReviewDialog({ id }: CreateReviewDialogProps) {
  const [open, setOpen] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);
  const { t } = useTranslation();

  const { data: existingReview } = useReview(id || 0);
  const { mutate: createReview, isPending: isCreating } = useCreateReview();
  const { mutate: updateReview, isPending: isUpdating } = useUpdateReview();

  const form = useForm<FormValues>({
    resolver: zodResolver(reviewSchema),
    defaultValues: {
      comment: '',
      rating: 0,
      userId: 0,
      matterId: undefined,
    },
  });

  const isSubmitting = isCreating || isUpdating;
  const isEdit = Boolean(id);

  useEffect(() => {
    if (open && id && existingReview) {
      form.reset({
        comment: existingReview.comment || '',
        rating: existingReview.rating || 0,
        userId: existingReview.userId || 0,
        matterId: existingReview.matterId ?? undefined,
      });
    }
  }, [open, id, existingReview, form]);

  const onSubmit = (
    values: FormValues,
    action: 'create' | 'update' | 'createAndContinue' = 'create',
  ) => {
    if (action === 'update' && id) {
      // userId can't change on update (backend omits it from the DTO).
      updateReview(
        {
          id,
          data: {
            comment: values.comment,
            rating: values.rating,
            matterId: values.matterId,
          },
        },
        {
          onSuccess: () => setOpen(false),
        },
      );
    } else {
      createReview(values, {
        onSuccess: () => {
          form.reset({ comment: '', rating: 0, userId: 0, matterId: undefined });
          if (action === 'create') setOpen(false);
        },
      });
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        {id ? (
          <Button size="sm" variant="outline" className="rounded-r-none">
            <Pen className="h-4 w-4" />
          </Button>
        ) : (
          <Button>
            {t('new')} {t('review')}
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent className="max-w-lg gap-0 p-0 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[#e2e8f0] bg-[#f8fafc] px-6 py-4">
          <div className="flex items-center gap-2">
            <Star className="h-5 w-5 text-yellow-500" />
            <AlertDialogTitle className="text-lg font-semibold">
              {isEdit ? t('update') : t('new')} {t('review')}
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription asChild>
            <button
              onClick={() => setOpen(false)}
              aria-label={t('closeDialog')}
              className="text-muted-foreground hover:text-foreground rounded-md p-1 transition-colors"
            >
              <X size={20} />
            </button>
          </AlertDialogDescription>
        </div>

        <Form {...form}>
          <form className="flex flex-col gap-5 px-6 py-5" noValidate>
            {/* Rating — interactive stars */}
            <FormField
              control={form.control}
              name="rating"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>{t('rating')} *</FormLabel>
                  <FormControl>
                    <div className="flex flex-wrap items-center gap-1 rounded-lg border border-[#e2e8f0] bg-[#fafafa] px-3 py-2.5">
                      {Array.from({ length: MAX_RATING }, (_, i) => i + 1).map(
                        (n) => {
                          const active = (hoverRating || field.value) >= n;
                          return (
                            <button
                              type="button"
                              key={n}
                              onMouseEnter={() => setHoverRating(n)}
                              onMouseLeave={() => setHoverRating(0)}
                              onClick={() => field.onChange(n)}
                              aria-label={`${n}/${MAX_RATING}`}
                              className="rounded p-0.5 transition-transform hover:scale-110"
                            >
                              <Star
                                className={cn(
                                  'h-6 w-6 transition-colors',
                                  active
                                    ? 'fill-yellow-400 text-yellow-400'
                                    : 'text-[#cbd5e1]',
                                )}
                              />
                            </button>
                          );
                        },
                      )}
                      <span className="text-muted-foreground ml-2 text-sm font-medium">
                        {field.value
                          ? `${field.value}/${MAX_RATING}`
                          : t('selectRating')}
                      </span>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* User + Case */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* User being reviewed */}
              <FormField
                control={form.control}
                name="userId"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>{t('user')} *</FormLabel>
                    <FormControl>
                      <UniversalCombobox
                        url="/users"
                        listUrl="/users/select-box"
                        labelKey="name"
                        valueKey="id"
                        placeholder={t('selectUser')}
                        selectedId={field.value || undefined}
                        disabled={isEdit}
                        onSelectItem={(user) => field.onChange(user?.['id'] || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Case (matter) — optional */}
              <FormField
                control={form.control}
                name="matterId"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>{t('matter')}</FormLabel>
                    <FormControl>
                      <UniversalCombobox
                        url="/matters"
                        listUrl="/matters/select-box"
                        labelKey="name"
                        valueKey="id"
                        placeholder={t('selectMatter')}
                        selectedId={field.value}
                        onSelectItem={(matter) =>
                          field.onChange(
                            matter ? (matter['id'] as number) : undefined,
                          )
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Comment */}
            <FormField
              control={form.control}
              name="comment"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel className="flex items-center gap-1.5">
                    <MessageSquare className="h-4 w-4" />
                    {t('comment')} *
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder={t('comment')}
                      rows={4}
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <AlertDialogFooter className="mt-1 flex justify-end gap-2">
              <AlertDialogCancel onClick={() => setOpen(false)}>
                {t('cancel')}
              </AlertDialogCancel>

              {isEdit ? (
                <Button
                  type="button"
                  disabled={isSubmitting}
                  onClick={form.handleSubmit((values) =>
                    onSubmit(values, 'update'),
                  )}
                >
                  {isSubmitting ? t('saving') : t('save')}
                </Button>
              ) : (
                <>
                  <Button
                    type="button"
                    disabled={isSubmitting}
                    variant="outline"
                    onClick={form.handleSubmit((values) =>
                      onSubmit(values, 'createAndContinue'),
                    )}
                  >
                    {isSubmitting ? t('saving') : t('saveAndCreate')}
                  </Button>
                  <Button
                    type="button"
                    disabled={isSubmitting}
                    onClick={form.handleSubmit((values) =>
                      onSubmit(values, 'create'),
                    )}
                  >
                    {isSubmitting ? t('saving') : t('save')}
                  </Button>
                </>
              )}
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
